import { useLanguage } from 'i18n/LanguageProvider';
import React, { useRef, VFC } from 'react';
import Highlighter from 'react-highlight-words';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';
import { RouteParams } from 'types';

import { useClickOutside } from 'hooks/useClickOutside';
import { useJobsSearch } from 'hooks/v2/useJobsSearch';

import { Skeleton } from '../../ui/skeleton';

export function SearchPopoverSkeleton({
  showSectionHeaders = false,
}: {
  showSectionHeaders?: boolean;
}) {
  return (
    <div className="absolute left-0 top-full mt-1 h-60 w-full cursor-default overflow-y-scroll rounded-lg border border-gray-100 bg-white pt-2 shadow-lg">
      {showSectionHeaders && (
        <p
          className="bg-gray-200 px-4 py-1.5 text-xs font-bold uppercase leading-4"
          data-testid="skeleton-header"
        >
          <FormattedMessage
            defaultMessage="Jobs"
            description="Section header for jobs in search results"
          />
        </p>
      )}
      {Array.from({ length: 5 }, (_, i) => (
        <div
          key={`skeleton-${i}`}
          className="flex h-16 w-full flex-col gap-1 px-4 py-3 text-start"
        >
          <Skeleton className="h-5 w-52" />
          <div className="flex h-4 items-center gap-1">
            <Skeleton className="h-4 w-40" />
            <p className="text-xs text-gray-300">|</p>
            <Skeleton className="h-4 w-40" />
          </div>
        </div>
      ))}
    </div>
  );
}

export interface SearchPopoverProps {
  searchString: string;
  setOpenPopover: React.Dispatch<React.SetStateAction<boolean>>;
  setInputText: (text: string) => void;
  scrollToJobs: () => void;
  filterTypes?: ('jobs' | 'positions' | 'locations')[];
  showSectionHeaders?: boolean;
  onLocationSelect?: (id: string, value: string) => void;
  onJobSelect?: (value: string) => void;
  specialLocations?: Array<{
    id: string;
    name: string;
    type: 'remote' | 'current';
    icon?: React.ReactNode;
    sublabel?: string;
  }>;
  ignoreClickOutsideElements?: React.RefObject<HTMLElement>[];
}

export const SearchPopover: VFC<SearchPopoverProps> = ({
  searchString,
  setOpenPopover,
  setInputText,
  filterTypes = ['jobs', 'positions', 'locations'],
  showSectionHeaders = true,
  onLocationSelect,
  onJobSelect,
  specialLocations = [],
  ignoreClickOutsideElements = [],
}) => {
  const { accountSlug, brandId } = useParams<RouteParams>();
  const { currentLanguage } = useLanguage();

  const popoverRef = useRef<HTMLDivElement>(null);
  useClickOutside(
    popoverRef,
    () => setOpenPopover(false),
    true,
    ignoreClickOutsideElements,
  );

  const { data, isFetching } = useJobsSearch(
    accountSlug,
    brandId,
    searchString,
    currentLanguage,
  );

  const handleSelect = (type: string, id: string, value: string) => {
    setOpenPopover(false);

    if (type === 'locations') {
      onLocationSelect?.(id, value);
    } else if (type === 'jobs') {
      onJobSelect?.(value);
    }

    setInputText(value);
  };

  if (isFetching) {
    return <SearchPopoverSkeleton showSectionHeaders={showSectionHeaders} />;
  }

  const isEmptySearch = !searchString.trim();

  const hasResults =
    !isEmptySearch &&
    data &&
    Object.entries(data)
      .filter(([type]) =>
        filterTypes.includes(type as 'jobs' | 'positions' | 'locations'),
      )
      .some(([, values]) => values && values.length > 0);

  if (isEmptySearch) {
    if (specialLocations.length === 0) {
      return null;
    }
  } else if (
    !data ||
    (!hasResults && !isFetching && specialLocations.length === 0)
  ) {
    return null;
  }

  return (
    <>
      <div
        ref={popoverRef}
        className="absolute left-0 top-full z-40 mt-1 max-h-60 w-full cursor-default overflow-y-scroll rounded-lg border border-gray-100 bg-white pt-2 shadow-lg"
      >
        {/* Special Locations (Remote, Current Location) */}
        {specialLocations.map(location => (
          <div key={location.id}>
            <button
              type="button"
              onClick={() =>
                handleSelect('locations', location.id, location.name)
              }
              className="hover:bg-primary-50 flex w-full flex-col gap-1 px-4 py-3 text-start text-sm font-normal leading-4 text-gray-800"
            >
              <div className="flex items-center gap-2">
                {location.icon && (
                  <span className="flex-shrink-0">{location.icon}</span>
                )}
                <div className="flex flex-col">
                  <span className="font-medium">{location.name}</span>
                  {location.sublabel && (
                    <span className="text-xs text-gray-500">
                      {location.sublabel}
                    </span>
                  )}
                </div>
              </div>
            </button>
          </div>
        ))}

        {!isEmptySearch &&
          data &&
          Object.entries(data)
            .filter(([type]) =>
              filterTypes.includes(type as 'jobs' | 'positions' | 'locations'),
            )
            .map(([type, values]) => (
              <span key={type}>
                {values && values.length > 0 && showSectionHeaders && (
                  <p
                    className="bg-gray-200 px-4 py-1.5 text-xs font-bold uppercase leading-4"
                    data-testid={`section-header-${type}`}
                  >
                    {type}
                  </p>
                )}
                {values.map(item => {
                  // Filter out duplicate current location entries from API
                  if (
                    type === 'locations' &&
                    specialLocations.some(
                      special =>
                        special.type === 'current' &&
                        (item.name?.toLowerCase().includes('current') ||
                          item.name?.toLowerCase().includes('ubicación')),
                    )
                  ) {
                    return null;
                  }

                  return (
                    <div key={item.external_id}>
                      <button
                        type="button"
                        onClick={() =>
                          handleSelect(type, item.external_id, item.name)
                        }
                        className="hover:bg-primary-50 flex w-full flex-col gap-1 px-4 py-3 text-start text-sm font-normal leading-4 text-gray-800"
                      >
                        <Highlighter
                          autoEscape
                          searchWords={[searchString]}
                          textToHighlight={item.name}
                          highlightClassName="bg-primary-50 text-primary"
                        />
                      </button>
                    </div>
                  );
                })}
              </span>
            ))}
      </div>
    </>
  );
};
